import { useCallback } from 'react';
import { usersApi } from '@/api/users';
import { useResource } from '@/hooks/useResource';
import type { CreateUserRequest, UpdateUserRequest, User } from '@/types/user';

/**
 * User list + operations (admin only)
 */
export function useUsers() {
  const fetcher = useCallback(async (signal: AbortSignal) => {
    const response = await usersApi.getAll(undefined, { signal });
    return response.users ?? [];
  }, []);

  const resource = useResource<User[]>(fetcher, { errorMessage: 'Failed to load users' });
  const { setData, reload } = resource;

  const createUser = useCallback(
    async (data: CreateUserRequest): Promise<User> => {
      const created = await usersApi.create(data);
      const user: User = { ...created, updated_at: created.updated_at ?? created.created_at };
      setData((prev) => [user, ...(prev ?? [])]);
      return user;
    },
    [setData]
  );

  const updateUser = useCallback(
    async (id: string, data: UpdateUserRequest): Promise<User> => {
      const updated = await usersApi.update(id, data);
      setData((prev) => prev?.map((user) => (user.id === id ? updated : user)));
      return updated;
    },
    [setData]
  );

  const deleteUser = useCallback(
    async (id: string): Promise<void> => {
      await usersApi.delete(id);
      setData((prev) => prev?.filter((user) => user.id !== id));
    },
    [setData]
  );

  return {
    users: resource.data ?? [],
    isLoading: resource.isLoading,
    error: resource.error,
    reload,
    createUser,
    updateUser,
    deleteUser,
  };
}
